import type { AnalyticsBrowser } from '@segment/analytics-next';

type SegmentProperties = object;

const PRODUCTION_WRITE_KEY = 'cym2AIxfG8o5i2RKaN4No2sYkrLb2aRO';
const STAGING_WRITE_KEY = '6wq1Ihdge8hN8jf03MxFyp00RdORQQf1';

// Segment holds events client-side until either threshold trips, so a burst of
// clicks leaves as one request instead of one per event. Both are required by
// the batching strategy. Payloads cap at 500 KB per batch / 32 KB per event.
const BATCH_SIZE = 20;
const BATCH_TIMEOUT_MS = 5000;

/**
 * Write keys are per-workspace, not per-environment variable: only the two
 * hosted domains report to Segment at all, and localhost/preview builds
 * deliberately resolve to null so they never pollute either workspace.
 */
function resolveWriteKey(hostname: string) {
  if (hostname.includes('clients.evisort')) return PRODUCTION_WRITE_KEY;
  if (hostname.includes('clients') || hostname.includes('feature-hot-fix')) {
    return STAGING_WRITE_KEY;
  }
  return null;
}

let segment: AnalyticsBrowser | null = null;
let isLoading = false;

// Calls made while the chunk is in flight wait here rather than being dropped,
// which is what the inlined snippet's array stub used to buy us. Never queue
// unless a load is actually pending, or hosts that resolve to no write key
// would accumulate every event for the life of the page.
const pending: Array<(analytics: AnalyticsBrowser) => void> = [];

export function loadSegment() {
  if (isLoading) return;

  const writeKey = resolveWriteKey(globalThis.location?.hostname ?? '');
  if (!writeKey) return;

  isLoading = true;

  import('@segment/analytics-next')
    .then(({ AnalyticsBrowser }) => {
      segment = AnalyticsBrowser.load(
        { writeKey },
        {
          integrations: {
            'Segment.io': {
              deliveryStrategy: {
                strategy: 'batching',
                config: { size: BATCH_SIZE, timeout: BATCH_TIMEOUT_MS },
              },
            },
          },
        },
      );

      for (const send of pending) send(segment);
      pending.length = 0;
    })
    .catch(() => {
      // Releasing the flag matters more than the queued calls: `withSegment`
      // only queues while a load is pending, so leaving it set would grow
      // `pending` for the rest of the session with nothing left to drain it.
      isLoading = false;
      pending.length = 0;
    });
}

function withSegment(send: (analytics: AnalyticsBrowser) => void) {
  if (segment) {
    send(segment);
    return;
  }
  if (isLoading) pending.push(send);
}

export function segmentIdentify(userId: string, traits: SegmentProperties) {
  withSegment((analytics) => {
    analytics.identify(userId, traits);
  });
}

export function segmentTrack(name: string, properties?: SegmentProperties) {
  withSegment((analytics) => {
    analytics.track(name, properties);
  });
}

export function segmentPage(properties?: SegmentProperties) {
  withSegment((analytics) => {
    if (properties && Object.keys(properties).length > 0) {
      analytics.page(properties);
      return;
    }
    analytics.page();
  });
}
